import { Link } from "react-router-dom";
import type { SalesMove } from "@sales-ops-amplify/shared";
import { NOT_RIGHT_REASONS } from "@sales-ops-amplify/shared";
import { formatDue, formatMoney, priorityLabel } from "./format";
import { SalesMoveEvidenceSection } from "./sales-move-evidence-section";
import { SalesMoveHistorySection } from "./sales-move-history-section";

type Props = {
  move: SalesMove;
  busy?: boolean;
  onPrimary: () => void;
  onLater: (preset: "later_today" | "tomorrow" | "next_business_day" | "waiting_for_buyer") => void;
  onNotRight: (reason: string) => void;
  onDismiss: () => void;
  onAskAi: () => void;
  onDelegate: () => void;
};

export function SalesMoveDetailPanel({
  move,
  busy,
  onPrimary,
  onLater,
  onNotRight,
  onDismiss,
  onAskAi,
  onDelegate,
}: Props) {
  const primaryLabel =
    move.actionDefinition?.label || move.recommendedAction;

  return (
    <aside className="flex h-full flex-col rounded-lg border border-border bg-card p-4">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Recommended move
      </p>
      <h2 className="mt-1 text-lg font-semibold text-foreground">{primaryLabel}</h2>

      <div className="mt-3 space-y-1 text-sm text-muted-foreground">
        {move.accountName ? (
          <p>
            <span className="text-foreground">{move.accountName}</span>
            {move.relevantPerson?.name
              ? ` · ${move.relevantPerson.name}`
              : ""}
            {move.relevantPerson?.role
              ? ` · ${move.relevantPerson.role}`
              : ""}
          </p>
        ) : null}
        {(move.dealStage || move.dealValue != null) && (
          <p>
            {[
              move.dealStage,
              move.dealValue != null
                ? formatMoney(move.dealValue, move.currency)
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        )}
        <p>
          {formatDue(move.dueAt)}
          {move.dueAt ? " · " : ""}
          {priorityLabel(move.priority)}
        </p>
      </div>

      <button
        type="button"
        disabled={busy}
        onClick={onPrimary}
        className="mt-4 w-full rounded-md bg-brand px-3 py-2.5 text-sm font-medium text-brand-foreground transition hover:opacity-90 disabled:opacity-50"
      >
        {primaryLabel}
      </button>

      <div className="mt-4 space-y-3 text-sm">
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Situation
          </h3>
          <p className="mt-1 text-foreground/90">{move.situationSummary}</p>
        </section>
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Why now
          </h3>
          <p className="mt-1 text-foreground/90">{move.whyNow}</p>
        </section>
        {move.expectedOutcome ? (
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Expected outcome
            </h3>
            <p className="mt-1 text-foreground/90">{move.expectedOutcome}</p>
          </section>
        ) : null}
        {move.riskOfWaiting ? (
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Risk of waiting
            </h3>
            <p className="mt-1 text-foreground/90">{move.riskOfWaiting}</p>
          </section>
        ) : null}
        {(move.sourceSignals?.length ?? 0) > 0 ? (
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Relevant signals
            </h3>
            <ul className="mt-1 list-disc space-y-1 pl-4 text-foreground/90">
              {move.sourceSignals!.slice(0, 3).map((s) => (
                <li key={s.id}>{s.label}</li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => onLater("later_today")}
          className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted/20"
        >
          Later today
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onLater("tomorrow")}
          className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted/20"
        >
          Tomorrow
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onDelegate}
          className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted/20"
        >
          Delegate
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onAskAi}
          className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted/20"
        >
          Ask AI
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onDismiss}
          className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted/20"
        >
          Already handled
        </button>
      </div>

      <details className="mt-3">
        <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
          Not right
        </summary>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {NOT_RIGHT_REASONS.map((reason) => (
            <button
              key={reason}
              type="button"
              disabled={busy}
              onClick={() => onNotRight(reason)}
              className="rounded border border-border px-2 py-1 text-[11px] hover:bg-muted/20"
            >
              {reason}
            </button>
          ))}
        </div>
      </details>

      <div className="mt-3 flex flex-wrap gap-3 text-xs">
        {move.sourceTasks?.[0]?.taskId ? (
          <Link
            to={`/tasks?taskId=${encodeURIComponent(move.sourceTasks[0].taskId)}`}
            className="font-medium text-brand hover:underline"
          >
            Source task
          </Link>
        ) : null}
        {move.dealId ? (
          <Link
            to={`/deals/${move.dealId}`}
            className="font-medium text-brand hover:underline"
          >
            Open deal
          </Link>
        ) : null}
        {move.dealId ? (
          <Link
            to="/dashboard?view=deal-analytics"
            className="font-medium text-brand hover:underline"
          >
            View pipeline analytics
          </Link>
        ) : (
          <Link
            to="/dashboard"
            className="font-medium text-brand hover:underline"
          >
            Open Dashboard
          </Link>
        )}
      </div>

      <SalesMoveHistorySection move={move} />
      <SalesMoveEvidenceSection move={move} />
    </aside>
  );
}
